<script setup lang="ts">
import { computed, ref } from 'vue';

/**
 * The image-management demo: a sortable gallery with a full-view modal.
 *
 * Converted from demoalbum.vue, and isotope-layout is gone. Isotope was doing
 * three things here — a fitRows layout, sorting by data attributes, and removing
 * an item then re-laying out. CSS grid handles layout for free, and sorting and
 * removal are list operations Vue already renders declaratively, so the
 * component no longer reads sort keys back out of the DOM it just wrote.
 *
 * That also removed the jQuery dependency for everything except the Semantic
 * modals.
 */

type SortKey = 'original' | 'name' | 'category' | 'date';

interface AlbumItem {
  id: number;
  name: string;
  filename: string;
  /** yyyymmdd, sortable as a string. */
  date: string;
  category: string;
}

const GALLERY_PATH = '/images/demo-gallery/';

const ITEMS: readonly AlbumItem[] = [
  { id: 101, name: 'Trekking', filename: 'mountain_trecking_in_sunset.png', date: '20141110', category: 'transition' },
  { id: 102, name: 'Mountain landscape', filename: 'landscape-mountains-nature-clouds.png', date: '20131010', category: 'transition' },
  { id: 103, name: 'Sunset on beach', filename: 'sunset-on-beach.png', date: '20151010', category: 'post-transition' },
  { id: 104, name: 'Canadian rockies', filename: 'canadian_rockies_emerald_lake.png', date: '20150823', category: 'post-transition' },
  { id: 105, name: 'Desert road', filename: 'road-sky-sand-street.png', date: '20140403', category: 'transition' },
  { id: 106, name: 'Autumn forest path', filename: 'autumn_path.png', date: '20150215', category: 'alkali' },
  { id: 107, name: 'Vermont autumn', filename: 'vermont_autumn.jpg', date: '20130707', category: 'alkali' },
  { id: 108, name: 'Aurora light', filename: 'aurora.jpg', date: '20151123', category: 'transition' },
  { id: 109, name: 'Yosemite peak', filename: 'yosemite.png', date: '20151229', category: 'transition' },
  { id: 110, name: 'Tropical beach', filename: 'tropical_beach.png', date: '20140813', category: 'alkaline-earth' },
  { id: 111, name: 'Eldorado national park', filename: 'eldorado_national_park.png', date: '20140517', category: 'alkali' },
  { id: 112, name: 'Tracks on the beach', filename: 'sunset-beach-traces.png', date: '20151010', category: 'transition' },
  { id: 113, name: 'Park bench in winter dawn', filename: 'snow-dawn-sunset-winter.png', date: '20150428', category: 'post-transition' },
  { id: 114, name: 'Winter street, black and white', filename: 'snow-black-and-white-street-winter.png', date: '20140329', category: 'post-transition' },
];

const removedIds = ref<Set<number>>(new Set());
const sortKey = ref<SortKey>('original');
const modalImage = ref<AlbumItem | null>(null);

const imageModal = ref<HTMLElement | null>(null);
const offersModal = ref<HTMLElement | null>(null);

const visibleItems = computed(() => {
  const remaining = ITEMS.filter((item) => !removedIds.value.has(item.id));

  switch (sortKey.value) {
    case 'name':
      return [...remaining].sort((a, b) => a.name.localeCompare(b.name, 'nb'));
    case 'category':
      return [...remaining].sort((a, b) => a.category.localeCompare(b.category, 'nb'));
    case 'date':
      return [...remaining].sort((a, b) => a.date.localeCompare(b.date));
    default:
      return remaining;
  }
});

function imageUrl(item: AlbumItem): string {
  return `${GALLERY_PATH}${item.filename}`;
}

function sortBy(key: SortKey): void {
  sortKey.value = key;
}

function openImage(item: AlbumItem): void {
  modalImage.value = item;

  if (imageModal.value) {
    window.$(imageModal.value).modal('show');
  }
}

function removeItem(item: AlbumItem): void {
  // A new Set so the computed dependency actually invalidates.
  removedIds.value = new Set(removedIds.value).add(item.id);
}

function openOffers(): void {
  if (offersModal.value) {
    window.$(offersModal.value).modal('show');
  }
}
</script>

<template>
  <div>
    <div class="ui compact buttons">
      <button class="ui button" :class="{ active: sortKey === 'original' }" @click="sortBy('original')">
        | Standard |
      </button>
      <button class="ui button" :class="{ active: sortKey === 'name' }" @click="sortBy('name')">
        Navn
      </button>
      <button class="ui button" :class="{ active: sortKey === 'category' }" @click="sortBy('category')">
        Kategori
      </button>
      <button class="ui button" :class="{ active: sortKey === 'date' }" @click="sortBy('date')">
        Dato
      </button>
    </div>

    <div class="demo-album">
      <figure v-for="item in visibleItems" :key="item.id" class="album-item">
        <div class="album-tools">
          <div class="ui small darkpurple icon buttons">
            <button class="ui button" title="Slett bilde" @click.stop="removeItem(item)">
              <i class="times icon" />
            </button>
            <button class="ui button" title="Rediger bilde" @click.stop="openOffers">
              <i class="paint brush icon" />
            </button>
          </div>
        </div>
        <button type="button" class="album-open" @click="openImage(item)">
          <img :src="imageUrl(item)" :alt="item.name" loading="lazy" />
        </button>
      </figure>
    </div>

    <div ref="imageModal" class="ui modal">
      <div class="header">{{ modalImage?.name }}</div>
      <div class="image scrolling content">
        <img v-if="modalImage" class="image" :src="imageUrl(modalImage)" :alt="modalImage.name" />
      </div>
    </div>

    <div ref="offersModal" class="ui mini modal">
      <div class="header">Bilderedigering</div>
      <div class="content">
        <p>Det du behøver...</p>
        <ul>
          <li>Automatisk nedskalering</li>
          <li>Kutt/klipp</li>
          <li>Roter</li>
          <li>Tegning, tekst, illustrering</li>
          <li>Filter</li>
        </ul>
        <p>Vi hører gjerne om deres ønsker og behov.</p>
        <div>
          <a app-navlink-actionname="contactus" href="/kontakt-oss" class="ui darkpurple button">
            Ta kontakt i dag!
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.demo-album {
  /* Replaces isotope's fitRows layout, and reflows on its own. */
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  margin-top: 10px;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
}

.album-item {
  position: relative;
  margin: 0;
}

.album-tools {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 1;
  opacity: 0;
  transition: opacity 150ms ease;
}

.album-item:hover .album-tools,
.album-item:focus-within .album-tools {
  opacity: 1;
}

.album-open {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.album-open img {
  display: block;
  width: 100%;
  height: 150px;
  object-fit: cover;
}

@media (prefers-reduced-motion: reduce) {
  .album-tools {
    transition: none;
  }
}
</style>
